import { Theme, Components } from '@mui/material/styles';
import tokens from '@verifime/design-tokens';
import { outfit } from '../../fonts/outfit';

export const font = outfit;

export const typographyTheme = {
  fontFamily: font.style.fontFamily,
  h1: {
    fontSize: tokens.fontSize8xl,
    fontWeight: tokens.fontWeightLight,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  h2: {
    fontSize: tokens.fontSize7xl,
    fontWeight: tokens.fontWeightLight,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  h3: {
    fontSize: tokens.fontSize4xl,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  h4: {
    fontSize: tokens.fontSize2xl,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  h5: {
    fontSize: tokens.fontSizeXl,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  h6: {
    fontSize: tokens.fontSizeBase,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  body1: {
    fontSize: tokens.fontSizeBase,
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  body2: {
    fontSize: tokens.fontSizeSm,
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  subtitle1: {
    fontSize: tokens.fontSizeSm,
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  subtitle2: {
    fontSize: tokens.fontSizeXs,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  overline: {
    fontSize: tokens.fontSizeXs,
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  caption: {
    fontSize: tokens.fontSizeXs,
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: tokens.letterSpacingReg,
    lineHeight: tokens.lineHeightShort,
  },
  // Override for default button (size: medium)
  button: {
    fontSize: tokens.fontSizeSm,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: tokens.letterSpacingLg,
    lineHeight: tokens.lineHeightDouble,
  },
};

export const paletteGrey = {
  '50': tokens.colorGrey050,
  '100': tokens.colorGrey100,
  '200': tokens.colorGrey200,
  '300': tokens.colorGrey300,
  '400': tokens.colorGrey400,
  '500': tokens.colorGrey500,
  '600': tokens.colorGrey600,
  '700': tokens.colorGrey700,
  '800': tokens.colorGrey800,
  '900': tokens.colorGrey900,
  A100: tokens.colorGreyA100,
  A200: tokens.colorGreyA200,
  A400: tokens.colorGreyA400,
  A700: tokens.colorGreyA700,
};

export const semanticBorderRadius = {
  sm: tokens.borderRadiusSm,
  lg: tokens.borderRadiusLg,
  xl: tokens.borderRadiusXl,
  round: tokens.borderRadiusRound,
};

export const semanticFontWeight = {
  light: tokens.fontWeightLight,
  regular: tokens.fontWeightRegular,
  semibold: tokens.fontWeightSemibold,
  bold: tokens.fontWeightBold,
  bolder: tokens.fontWeightBolder,
};

export const semanticFontSize = {
  xs: tokens.fontSizeXs,
  sm: tokens.fontSizeSm,
  base: tokens.fontSizeBase,
  lg: tokens.fontSizeLg,
  // Note: 'l' (1.25rem) is not available in Default tokens, using xl (1.25rem) as equivalent
  l: tokens.fontSizeXl, // Using xl as equivalent (both are 1.25rem)
  xl: tokens.fontSizeXl,
  '2xl': tokens.fontSize2xl,
  '3xl': tokens.fontSize3xl,
  '4xl': tokens.fontSize4xl,
  '5xl': tokens.fontSize5xl,
  '6xl': tokens.fontSize6xl,
  '7xl': tokens.fontSize7xl,
  '8xl': tokens.fontSize8xl,
};

export const semanticLetterSpacing = {
  xs: tokens.letterSpacingXs,
  sm: tokens.letterSpacingSm,
  reg: tokens.letterSpacingReg,
  lg: tokens.letterSpacingLg,
  xl: tokens.letterSpacingXl,
};

export const semanticLineHeight = {
  single: tokens.lineHeightSingle,
  short: tokens.lineHeightShort,
  long: tokens.lineHeightLong,
  double: tokens.lineHeightDouble,
  // Note: 'standard' is not available in Default tokens (specific to Clarity theme), using short (1.2) as equivalent
  standard: tokens.lineHeightShort, // Using short as equivalent (both are 1.2)
};

export const semanticSpacing = {
  none: tokens.spacingNone,
  '4xs': tokens.spacing4xs,
  '3xs': tokens.spacing3xs,
  '2xs': tokens.spacing2xs,
  xs: tokens.spacingXs,
  sm: tokens.spacingSm,
  base: tokens.spacingBase,
  lg: tokens.spacingLg,
  '2lg': tokens.spacing2lg,
  xl: tokens.spacingXl,
  '2xl': tokens.spacing2xl,
  '3xl': tokens.spacing3xl,
  '4xl': tokens.spacing4xl,
  '5xl': tokens.spacing5xl,
  '6xl': tokens.spacing6xl,
  '7xl': tokens.spacing7xl,
};

export const semanticSize = {
  '8xs': tokens.size8xs,
  '7xs': tokens.size7xs,
  '6xs': tokens.size6xs,
  '5xs': tokens.size5xs,
  '4xs': tokens.size4xs,
  '3xs': tokens.size3xs,
  '2xs': tokens.size2xs,
  xs: tokens.sizeXs,
  sm: tokens.sizeSm,
  md: tokens.sizeMd,
  lg: tokens.sizeLg,
  xl: tokens.sizeXl,
};

export const commonComponentsOverride: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    variants: [
      {
        props: { size: 'small' },
        style: {
          fontSize: tokens.fontSizeXs,
          fontWeight: tokens.fontWeightSemibold,
          letterSpacing: tokens.letterSpacingLg,
          lineHeight: tokens.lineHeightDouble,
        },
      },
      {
        props: { size: 'large' },
        style: {
          fontSize: tokens.fontSizeBase,
          fontWeight: tokens.fontWeightSemibold,
          letterSpacing: tokens.letterSpacingLg,
          lineHeight: tokens.lineHeightDouble,
        },
      },
    ],
  },
};
